import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { scrollTo } from "@/lib/scroll";
import { CONTENT } from "@/content";

const LINKS = [
  { label: "MUSIC", target: "#music", id: "music" },
  { label: "ABOUT", target: "#about", id: "about" },
  { label: "SHOWS", target: "#shows", id: "shows" },
  { label: "VISUALS", target: "#visuals", id: "visuals" },
  { label: "CONTACT", target: "#contact", id: "contact" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 48);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (target) => {
    const wasOpen = open;
    setOpen(false);
    setTimeout(() => scrollTo(target), wasOpen ? 420 : 0);
  };

  return (
    <>
      <header
        data-testid="main-nav"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled ? "py-3 bg-[#070709]/80 backdrop-blur-md border-b border-white/5" : "py-6 bg-transparent"
        }`}
      >
        <div className="flex items-center justify-between px-6 md:px-12">
          <button
            data-testid="nav-logo"
            onClick={() => go("#top")}
            className="font-display font-extrabold tracking-tight text-lg md:text-xl text-[#f5f3ef] hover:text-[#70c7fa] transition-colors duration-300"
          >
            {CONTENT.artist}
          </button>

          <nav className="hidden md:flex items-center gap-9">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => go(l.target)}
                className="font-mono2 text-[11px] tracking-[0.3em] text-[#a3a3ac] hover:text-[#70c7fa] transition-colors duration-300"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <button
            data-testid="mobile-menu-btn"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
            className="md:hidden flex flex-col gap-1.5 p-2"
          >
            <span className={`block w-7 h-px bg-[#f5f3ef] transition-transform duration-300 ${open ? "rotate-45 translate-y-[3.5px]" : ""}`} />
            <span className={`block w-7 h-px bg-[#f5f3ef] transition-transform duration-300 ${open ? "-rotate-45 -translate-y-[3.5px]" : ""}`} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-0 z-40 bg-[#070709] flex flex-col justify-center px-8"
          >
            <nav className="flex flex-col gap-2">
              {LINKS.map((l, i) => (
                <div key={l.id} className="overflow-hidden">
                  <motion.button
                    data-testid={`mobile-nav-link-${l.id}`}
                    initial={{ y: "110%" }}
                    animate={{ y: 0 }}
                    exit={{ y: "110%" }}
                    transition={{ duration: 0.7, delay: 0.06 * i, ease: [0.16, 1, 0.3, 1] }}
                    onClick={() => go(l.target)}
                    className="font-display font-extrabold uppercase text-[10.5vw] leading-[1.1] whitespace-nowrap tracking-tight text-[#f5f3ef] hover:text-[#70c7fa] transition-colors duration-300"
                  >
                    {l.label}
                  </motion.button>
                </div>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ delay: 0.4 }}
              className="mt-14 font-mono2 text-[11px] tracking-[0.3em] text-[#64646e]"
            >
              <a href={CONTENT.instagram.url} target="_blank" rel="noopener noreferrer" className="hover:text-[#70c7fa] transition-colors">
                INSTAGRAM {CONTENT.instagram.handle}
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
